import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {Button, Checkbox} from '@/web-components';

import DropdownV2 from '../../../shared/components/dropdowns/dropdown';
import DirectApi from '../../../../directApi';
import {
	ConfigImportActions,
	ConfigImportRow,
	ConfigImportRows,
	ConfigImportSection,
	ConfigImportStatus,
} from '../xero/AppXeroStyled';
import {ConfigImportOptionLabel, ConfigImportOrganisation} from './QuickbooksConfigurationImportStyled';
import {loadQuickbooksCompanies} from './loadQuickbooksCompanies';

/**
 * The three configuration types (T4-179 AC3). The configGroup values are a contract with the
 * imports that write them (T4-181, T4-182, T4-183): a mismatch reads "Never imported" for ever.
 * T4-180 adds each entry's importPath.
 */
export const CONFIG_TYPES = [
	{
		key: 'ACCOUNTS_AND_TAX',
		configGroup: 'QBO_ACCOUNTS_AND_TAX',
		labelId: 'settings.app_catalog.quickbooks.config_accounts_and_tax',
	},
	{
		key: 'PRODUCTS_AND_SERVICES',
		configGroup: 'QBO_PRODUCTS_AND_SERVICES',
		labelId: 'settings.app_catalog.quickbooks.config_products_and_services',
	},
	{
		key: 'CLASSES',
		configGroup: 'QBO_CLASSES',
		labelId: 'settings.app_catalog.quickbooks.config_classes',
	},
];

export const IMPORT_STATUS_PATH = 'quickbooks/configuration/import-status';

// Both sources can hand over something that is not a list: the page stores a failed call's error
// body ({status, message, correlationId}) as its list. Anything but an array is no organisations.
const asList = value => (Array.isArray(value) ? value : []);

const organisationLabel = org => org.name || org.realmId;

/**
 * Import QuickBooks Configuration (T4-179, spec 030). Reads only: the button is wired in T4-180.
 *
 * multiTenant - the page's quickbooks_multi_tenant flag. When on, the page has already fetched the
 *   organisation list and passes it in (organisations / organisationsLoading), so this section
 *   never repeats that call (D4). When off, the page never fetches it, so the section does, once.
 */
const QuickbooksConfigurationImport = ({multiTenant = false, organisations, organisationsLoading = false}) => {
	const {formatMessage, formatDate, formatTime} = useIntl();

	const [fetchedOrganisations, setFetchedOrganisations] = useState([]);
	const [chosenRealmId, setChosenRealmId] = useState(null);
	// null = the status has not answered yet. Until it does, the rows show no status text: "Never imported"
	// is a claim, and we do not know yet (spec R5).
	const [statusRows, setStatusRows] = useState(null);
	const [statusUnavailable, setStatusUnavailable] = useState(false);
	const [selectedKeys, setSelectedKeys] = useState([]);

	useEffect(() => {
		let active = true;
		DirectApi.Fetch_Raw(IMPORT_STATUS_PATH)
			.then(result => (result.status === 200 ? result.json() : null))
			.then(rows => {
				if (!active) {
					return;
				}
				// Only a list is an answer. A failed read, or a body we do not recognise, must not
				// fall through to "Never imported" (spec R5).
				if (Array.isArray(rows)) {
					setStatusRows(rows);
					setStatusUnavailable(false);
				} else {
					setStatusUnavailable(true);
				}
			})
			.catch(() => {
				if (active) {
					setStatusUnavailable(true);
				}
			});
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		if (multiTenant) {
			return;
		}
		let active = true;
		loadQuickbooksCompanies(path => DirectApi.Fetch_Raw(path)).then(list => {
			if (active) {
				setFetchedOrganisations(list);
			}
		});
		return () => {
			active = false;
		};
	}, [multiTenant]);

	if (multiTenant && organisationsLoading) {
		return null;
	}

	const organisationList = asList(multiTenant ? organisations : fetchedOrganisations);
	// Nothing to import into. The page already offers Add directly above this section.
	if (organisationList.length === 0) {
		return null;
	}

	// Derived on every render rather than set in an effect: the first organisation is the
	// selection from the first render that has a list - including a list that arrives as a prop
	// after mount - and again whenever the chosen one is no longer in the list.
	const activeOrganisation = organisationList.find(org => org.realmId === chosenRealmId) || organisationList[0];
	const activeRealmId = activeOrganisation.realmId;

	const describe = configType => {
		if (statusUnavailable) {
			return formatMessage({id: 'settings.app_catalog.quickbooks.config_status_unavailable'});
		}
		if (statusRows === null) {
			return '';
		}
		const row = (statusRows || []).find(
			candidate => candidate.configGroup === configType.configGroup && candidate.organisationExternalId === activeRealmId
		);
		if (!row) {
			return formatMessage({id: 'settings.app_catalog.quickbooks.config_never_imported'});
		}
		if (row.status !== 'SUCCESS') {
			return formatMessage({id: 'settings.app_catalog.quickbooks.config_import_failed'});
		}
		const when = row.lastImportedAt ? new Date(row.lastImportedAt) : null;
		if (!when || Number.isNaN(when.getTime())) {
			// A success we cannot date is not knowledge we can show.
			return formatMessage({id: 'settings.app_catalog.quickbooks.config_status_unavailable'});
		}
		return formatMessage(
			{id: 'settings.app_catalog.quickbooks.config_last_imported'},
			{
				// react-intl 3.x drops dateStyle from its options whitelist without erroring, so
				// the parts are named explicitly - the same format as the Xero page.
				timestamp: `${formatDate(when, {day: 'numeric', month: 'short', year: 'numeric'})}, ${formatTime(when)}`,
			}
		);
	};

	const toggle = key =>
		setSelectedKeys(current => (current.includes(key) ? current.filter(k => k !== key) : [...current, key]));

	const organisationFieldLabel = formatMessage({id: 'common.organization'});

	return (
		<ConfigImportSection>
			<h3>{formatMessage({id: 'settings.app_catalog.quickbooks.config_import_title'})}</h3>
			<p>{formatMessage({id: 'settings.app_catalog.quickbooks.config_import_description'})}</p>

			{multiTenant && organisationList.length > 1 ? (
				<DropdownV2
					label={organisationFieldLabel}
					value={activeRealmId}
					onChange={option => setChosenRealmId(option && option.value)}
					options={organisationList.map(org => ({value: org.realmId, label: organisationLabel(org)}))}
				/>
			) : (
				<ConfigImportOrganisation>
					<span className="qbo-config-organisation-label">{organisationFieldLabel}</span>
					<span>{organisationLabel(activeOrganisation)}</span>
				</ConfigImportOrganisation>
			)}

			<ConfigImportRows>
				{CONFIG_TYPES.map(configType => (
					<ConfigImportRow key={configType.key}>
						{/* The shared Checkbox takes no id, so the label cannot point at it with htmlFor.
						    The label wraps it instead, which names the box and makes the text clickable. */}
						<ConfigImportOptionLabel>
							<Checkbox checked={selectedKeys.includes(configType.key)} onClick={() => toggle(configType.key)} />
							{formatMessage({id: configType.labelId})}
						</ConfigImportOptionLabel>
						<ConfigImportStatus>{describe(configType)}</ConfigImportStatus>
					</ConfigImportRow>
				))}
			</ConfigImportRows>

			<ConfigImportActions>
				{/* Always disabled in T4-179: no import endpoint exists yet. T4-180 replaces this with
				    "disabled until an option is selected, and while importing" and wires onClick. */}
				<Button size={Button.SIZE.STANDARD} variant={Button.VARIANT.GREEN_FILLED} disabled>
					{formatMessage({id: 'settings.app_catalog.quickbooks.config_import_button'})}
				</Button>
			</ConfigImportActions>
		</ConfigImportSection>
	);
};

export default QuickbooksConfigurationImport;
